import { useMemo } from 'react';
import { useLeads } from '../hooks/useLeads';
import { summarize, weeklyKpis } from '../lib/metrics';
import { useContent } from '../hooks/useContent';
import KpiTile from '../components/KpiTile';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend
} from 'recharts';

// Binary One brand-aligned chart palette (greens fading into accents).
const PIE_COLORS = ['#00A651', '#008F46', '#006F37', '#3BBE6F', '#0F0F0F', '#f59e0b', '#d97706', '#7c3aed'];

export default function Dashboard() {
  const { leads, loading, error } = useLeads();
  const { targets } = useContent();
  const sum = useMemo(() => summarize(leads), [leads]);
  const weeks = useMemo(() => weeklyKpis(leads, { weeks: 12 }), [leads]);

  if (loading) return <div className="p-8 text-slate-500">Loading dashboard…</div>;

  if (error?.code === 'permission-denied') {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <h1 className="text-2xl font-semibold mb-3">Dashboard</h1>
        <div className="card p-6 border-red-200 bg-red-50 text-red-900">
          <div className="font-semibold mb-1">Dashboard can't load — Firestore rules not deployed</div>
          <div className="text-sm">
            Your account is signed in but Firestore is denying reads. See the red banner at the top of the page for the one-line fix
            (<span className="font-mono">firebase deploy --only firestore:rules</span> from <span className="font-mono">C:\APP</span>).
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <h1 className="text-2xl font-semibold mb-3">Dashboard</h1>
        <div className="card p-6 border-amber-200 bg-amber-50 text-amber-900">
          <div className="font-semibold mb-1">Couldn't reach the database</div>
          <div className="text-sm font-mono">{error.code || error.message}</div>
        </div>
      </div>
    );
  }

  const fmtKES = (n) => `KES ${Math.round(n).toLocaleString()}`;
  const winRatePct = `${Math.round(sum.winRate * 100)}%`;

  return (
    <div className="max-w-[1400px] mx-auto p-4 space-y-6">
      <h1 className="text-2xl font-semibold">Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label="Open leads" value={sum.openLeads} sub={`${sum.totalLeads} total`} />
        <KpiTile label="Pipeline value" value={fmtKES(sum.totalPipelineKES)} sub="monthly recurring" accent="emerald" />
        <KpiTile label="Won (MRR)" value={fmtKES(sum.wonKES)} sub={`${sum.wonCount} closed-won`} accent="emerald" />
        <KpiTile label="Win rate" value={winRatePct} sub={`${sum.wonCount} won / ${sum.lostCount} lost`} accent="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-4 lg:col-span-2">
          <div className="font-medium mb-2">Stage funnel (count)</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={sum.byStage}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="stage" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#00A651" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card p-4">
          <div className="font-medium mb-2">Lead source mix</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={sum.bySource} dataKey="count" nameKey="key" outerRadius={80} label>
                {sum.bySource.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-4">
          <div className="font-medium mb-2">Pipeline value by owner (KES/mo)</div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={sum.byOwner}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="key" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v => fmtKES(v)} />
              <Bar dataKey="valueKES" fill="#006F37" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card p-4">
          <div className="font-medium mb-2">Pipeline value by product (KES/mo)</div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={sum.byProduct}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="key" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={v => fmtKES(v)} />
              <Bar dataKey="valueKES" fill="#f59e0b" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card p-4">
        <div className="font-medium mb-2">12-Week KPI scorecard (vs. targets)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Week</th>
                <th className="text-right px-3 py-2">New prospects ({targets.newProspectsEnriched}/wk)</th>
                <th className="text-right px-3 py-2">Qualified</th>
                <th className="text-right px-3 py-2">Presentations</th>
                <th className="text-right px-3 py-2">Proposals</th>
                <th className="text-right px-3 py-2">Retainers Won</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map(w => (
                <tr key={w.key} className="border-t">
                  <td className="px-3 py-1.5">{w.key}</td>
                  <td className="px-3 py-1.5 text-right">
                    <span className={w.newProspectsEnriched >= targets.newProspectsEnriched ? 'text-emerald-700 font-medium' : ''}>
                      {w.newProspectsEnriched}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right">{w.qualifiedLeads}</td>
                  <td className="px-3 py-1.5 text-right">{w.founderPresentations}</td>
                  <td className="px-3 py-1.5 text-right">{w.proposalsSent}</td>
                  <td className="px-3 py-1.5 text-right font-medium">{w.retainersClosed}</td>
                </tr>
              ))}
              <tr className="border-t bg-slate-50 font-medium">
                <td className="px-3 py-2">TOTAL</td>
                {['newProspectsEnriched','qualifiedLeads','founderPresentations','proposalsSent','retainersClosed'].map(k => (
                  <td key={k} className="px-3 py-2 text-right">{weeks.reduce((s,w)=>s+w[k],0)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
